import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

const IntroductionPage = () => {
  const navigate = useNavigate();
  const [musicPlaying, setMusicPlaying] = useState(false);
  const [voicePlaying, setVoicePlaying] = useState(false);
  const musicRef = useRef(null);
  const voiceRef = useRef(null);

  useEffect(() => {
    // Try to auto-play background music when component mounts
    const tryAutoPlay = async () => {
      if (musicRef.current) {
        try {
          await musicRef.current.play();
          setMusicPlaying(true);
        } catch (err) {
          console.log('Auto-play prevented by browser. User must click to start audio:', err);
          setMusicPlaying(false);
        }
      }
    };
    
    // Small delay to ensure audio element is ready
    const timer = setTimeout(tryAutoPlay, 500);
    return () => clearTimeout(timer);
  }, []);

  const handleMusicToggle = async () => {
    if (musicRef.current) {
      try {
        if (musicPlaying) {
          musicRef.current.pause();
          setMusicPlaying(false);
        } else {
          await musicRef.current.play();
          setMusicPlaying(true);
        }
      } catch (err) {
        console.error('Error toggling music:', err);
      }
    }
  };

  const handleVoiceToggle = async () => {
    if (voiceRef.current) {
      try {
        if (voicePlaying) {
          voiceRef.current.pause();
          voiceRef.current.currentTime = 0;
          setVoicePlaying(false);
        } else {
          // Don't pause music - let both play together
          await voiceRef.current.play();
          setVoicePlaying(true);
        }
      } catch (err) {
        console.error('Error toggling voice:', err);
      }
    }
  };

  const handleContinue = () => {
    navigate('/privacy-notice');
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center relative overflow-hidden" style={{ maxWidth: '100vw' }}>
      {/* Full Background Image - 9:16 vertical format (portrait), used as-is */}
      <div 
        className="absolute inset-0"
        style={{
          backgroundImage: 'url(/assets/page1/background.png)',
          backgroundSize: 'contain',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          backgroundColor: '#E8E4DF'
        }}
      />

      {/* Audio Elements */}
      <audio
        ref={musicRef}
        preload="auto"
        loop
        onPlay={() => setMusicPlaying(true)}
        onPause={() => setMusicPlaying(false)}
        onError={(e) => console.error('Music loading error:', e)}
      >
        <source src="/assets/page1/coastal-music.mp3" type="audio/mpeg" />
        Your browser does not support the audio element.
      </audio>
      <audio
        ref={voiceRef}
        preload="auto"
        onPlay={() => setVoicePlaying(true)}
        onEnded={() => setVoicePlaying(false)}
        onError={(e) => console.error('Voice loading error:', e)}
      >
        <source src="/assets/page1/welcome-voice-new.mp3" type="audio/mpeg" />
        Your browser does not support the audio element.
      </audio>

      {/* Audio Controls */}
      <div className="absolute top-6 right-6 z-10">
        <button
          onClick={handleMusicToggle}
          className="bg-white/80 backdrop-blur-sm px-4 py-2 rounded-full text-sm hover:bg-white transition-all shadow-md"
          style={{ fontFamily: 'IBM Plex Serif, serif' }}
          data-testid="toggle-music-btn"
        >
          {musicPlaying ? '🔊 Music On' : '🔇 Music Off'}
        </button>
      </div>

      {/* Main Content - positioned to align with background elements */}
      <div className="relative z-10 flex flex-col items-center justify-center h-full w-full px-8">
        {/* Spacer to position content in lower portion */}
        <div className="flex-grow"></div>
        
        {/* Play Welcome Button - positioned above Let's settle in, font size 14px */}
        <div className="text-center mt-auto mb-3">
          <button
            onClick={handleVoiceToggle}
            className="text-gray-800 hover:text-gray-600 transition-all underline decoration-1 underline-offset-2"
            style={{ fontFamily: 'IBM Plex Serif, serif', fontSize: '14px' }}
            data-testid="toggle-voice-btn"
          >
            {voicePlaying ? '⏸ Pause Welcome' : '▶ Play Welcome'}
          </button>
        </div>
        
        {/* Let's settle in - positioned below Play Welcome, font size 14px */}
        <div className="text-center mb-8">
          <button
            onClick={handleContinue}
            className="text-gray-800 hover:text-gray-600 transition-all underline decoration-1 underline-offset-2"
            style={{ fontFamily: 'IBM Plex Serif, serif', fontSize: '14px' }}
            data-testid="continue-btn"
          >
            Let's settle in
          </button>
        </div>
      </div>
    </div>
  );
};

export default IntroductionPage;